import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { User } from '@lingugeek/schemas';
import { useAuthStore } from '../stores/auth-store';
import { getApiService } from '../api/services';
import { QUERY_KEYS } from '../types';

export function useAuth() {
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isLoading, error, actions } = useAuthStore();

  // Query for user data with React Query
  const userQuery = useQuery({
    queryKey: QUERY_KEYS.user,
    queryFn: async () => {
      const apiService = getApiService();
      const userData = await apiService.getUser();
      actions.login(userData);
      return userData;
    },
    enabled: false, // We'll trigger this manually or via store actions
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: (updates: Partial<User>) => actions.updateUser(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user });
    },
    onError: error => {
      actions.setError(
        error instanceof Error ? error.message : 'Failed to update user'
      );
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: () => actions.logout(),
    onSuccess: () => {
      queryClient.clear(); // Clear all cached data on logout
    },
    onError: error => {
      actions.setError(
        error instanceof Error ? error.message : 'Logout failed'
      );
    },
  });

  // Refresh user mutation
  const refreshUserMutation = useMutation({
    mutationFn: () => actions.refreshUser(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user });
    },
    onError: error => {
      actions.setError(
        error instanceof Error ? error.message : 'Failed to refresh user'
      );
    },
  });

  return {
    // State
    user,
    isAuthenticated,
    isLoading: isLoading || userQuery.isLoading,
    error:
      error ||
      (userQuery.error instanceof Error ? userQuery.error.message : null),

    // Actions
    login: actions.login,
    logout: logoutMutation.mutateAsync,
    updateUser: updateUserMutation.mutateAsync,
    refreshUser: refreshUserMutation.mutateAsync,

    // Mutation states
    isUpdatingUser: updateUserMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isRefreshing: refreshUserMutation.isPending,

    // Raw query/mutations for advanced use cases
    userQuery,
    updateUserMutation,
    logoutMutation,
    refreshUserMutation,
  };
}

// Hook for checking authentication status
export function useAuthStatus() {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  return {
    isAuthenticated,
    isLoading,
    hasUser: !!user,
  };
}

// Hook for user data only (without actions)
export function useUser() {
  const { user, isLoading, error } = useAuthStore();

  return {
    user,
    isLoading,
    error,
  };
}
